/**
 * api/fieldOverrides.js — Phase 1 booking form designer API.
 *
 * Reads/writes lbs_booking_field_overrides. RLS scopes to client_id;
 * super_admin sees all. Used by:
 *   - /admin/form-designer (editor UI)
 *   - useBookingForm hook (runtime merge into the static registry)
 */
import { supabase } from "@/lib/supabaseClient";

/** Fetch every override for one tenant. Returns [] when none. */
export async function listFieldOverrides(clientId) {
  if (!clientId) return { data: [], error: null };
  return supabase
    .from("lbs_booking_field_overrides")
    .select("*")
    .eq("client_id", clientId);
}

/** Public booking page: field keys the tenant marked Required (migration 171). */
export function publicRequiredFields(slug) {
  return supabase.rpc("lbs_public_required_fields", { p_slug: slug });
}


/** Upsert one override row. Engine reads (scope, field_key) → unique. */
export async function upsertFieldOverride(row) {
  const payload = {
    client_id:    row.client_id,
    scope:        row.scope,
    field_key:    row.field_key,
    label:        row.label ?? null,
    tab_key:      row.tab_key ?? null,
    position:     row.position ?? null,
    is_required:  row.is_required ?? null,
    is_visible:   row.is_visible ?? null,
    group_key:    row.group_key ?? null,
    is_qa_trigger: row.is_qa_trigger ?? null,
  };
  // Only include help_text when it has an actual value, so non-help-text
  // saves (dividers, tabs, plain field renames…) succeed even on
  // tenants whose DB hasn't yet applied migration 084 (the help_text
  // column would otherwise PGRST204 the upsert from missing schema cache).
  if (row.help_text != null && row.help_text !== "") {
    payload.help_text = row.help_text;
  }
  if (row.id) {
    return supabase
      .from("lbs_booking_field_overrides")
      .update(payload)
      .eq("id", row.id)
      .select()
      .maybeSingle();
  }
  return supabase
    .from("lbs_booking_field_overrides")
    .upsert(payload, { onConflict: "client_id,scope,field_key" })
    .select()
    .maybeSingle();
}

/** Reset (delete) one override row → fall back to registry defaults. */
export async function deleteFieldOverride(id) {
  return supabase.from("lbs_booking_field_overrides").delete().eq("id", id).select();
}

/**
 * Pure merge helpers. Take static registry arrays + override rows,
 * return effective arrays the renderer can iterate. Exported so the
 * form designer can preview live and the V2 shell can apply at render.
 */
/**
 * Merge static `TABS` registry with tenant overrides.
 *
 * Three behaviours:
 *   1. Registry tab + override row → relabel / hide using the override.
 *   2. Registry tab + no override   → leave as-is.
 *   3. Override row with no matching registry tab → CUSTOM TAB (Phase 7.3).
 *      Tenant-created tab. Rendered after all registry tabs (per design
 *      decision: "always at the end"), sorted by `position` then label.
 */
export function applyTabOverrides(tabs, overrides) {
  const tabOverrides = (overrides || []).filter((o) => o.scope === "tab");
  const byKey = {};
  for (const o of tabOverrides) byKey[o.field_key] = o;

  const registryKeys = new Set(tabs.map((t) => t.key));
  const merged = tabs
    .map((t) => {
      const o = byKey[t.key];
      if (!o) return t;
      if (o.is_visible === false) return null;
      return { ...t, label: o.label || t.label };
    })
    .filter(Boolean);

  // Custom tabs = override rows whose field_key isn't a registry tab key.
  const customTabs = tabOverrides
    .filter((o) => !registryKeys.has(o.field_key) && o.is_visible !== false)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.label || "").localeCompare(b.label || ""))
    .map((o) => ({
      key: o.field_key,
      label: o.label || o.field_key,
      isCustom: true,
    }));

  return [...merged, ...customTabs];
}

/**
 * Pure rendering helper used by the Shell to filter slots by tenant
 * visibility. Returns a fresh array; drops any slot with an override
 * row scope='slot' is_visible=false. Essential slots are dropped from
 * consideration in the designer UI (cannot be hidden), but if a
 * hidden row exists in DB for one anyway it'll still be honoured here
 * — defensive, no surprises.
 */
export function applySlotOverrides(slots, overrides) {
  const hidden = new Set(
    (overrides || [])
      .filter((o) => o.scope === "slot" && o.is_visible === false)
      .map((o) => o.field_key)
  );
  return slots.filter((s) => !hidden.has(s.key));
}

export function applyFieldOverrides(fields, overrides) {
  const byKey = {};
  for (const o of overrides || []) if (o.scope === "field") byKey[o.field_key] = o;
  return fields
    .map((f) => {
      const o = byKey[f.key];
      if (!o) return f;
      if (o.is_visible === false && !f.alwaysRequired) return null;
      return {
        ...f,
        label:     o.label    || f.label,
        tab:       o.tab_key  || f.tab,
        position:  o.position ?? f.position,
        required:  o.is_required ?? f.required ?? false,
        group_key: o.group_key ?? f.group_key ?? null,
        help_text: o.help_text || null,
      };
    })
    .filter(Boolean);
}
